import { useEffect, useState } from "react";
import TransactionCard from "../../../../components/ui/TransactionCard";
import TransactionFilter from "./TransactionFilters";
import {
  Withdrawhistorylistdetails,
  Withdrawhistorylistresponse,
} from "../../models/response/withdrawHistoryListResponse";
import localization from "../../../../config/localization";
import { toastService as toast } from "../../../../components/ui/toast/toast.service";
import { TransactionStatusTypes } from "../../../../models/constants/transactionStatusTypes";
import { UserHistoryListRequest } from "../../../../models/Request/userHistoryListRequest";
import {
  addUsersWithdrawTransactionUpdateRequest,
  getUserLastWithdrawal,
  getWithdrawHistory,
  uploadTransactionUpdateDocument,
  UserWithdrawRequestUpdateStatus,
  validateUsersWithdrawTransactionUpdateById,
} from "../../services/transactions.service";
import InfiniteScroll from "../../../../components/ui/InfiniteScroll";
import { Button } from "../../../../components/ui";
import { formatUtcToLocalDateTime } from "../../../../utils/dateTimeHelper";
import { BLOB_IMAGE_URL } from "../../../../config/appConfig";
import { BlobContainerConsts } from "../../../../models/constants/blobContainerConsts";
import { ValidateTransactionUpdateResponse } from "../../models/response/validateTransactionUpdateResponse";
import { LastWithdrawalResponse } from "../../models/response/lastWithdrawalResponse";
import { ConfirmationModal } from "../../../../components/ui/modals";
import { Withdrawcancelrequest } from "../../../../models/Request/withdrawCancelRequest";
import { UploadDocumentModal } from "../../../../components/ui/modals/UploadDocumentModal";
import { UploadDocumentRequest } from "../../models/request/uploadDocumentRequest";
import { WithdrawTransactionUpdateRequest } from "../../models/request/withdrawTransactionUpdateRequest";
import { WITHDRAW_STATUS_STYLE } from "../../models/constants/statusStyles";
import { useRealTimeStore } from "../../../../store/signalrRealTime.store";
import { userPendingStore } from "../../../users/store/userPendingData.store";
import common from "../../../../utils/common";
import { useAppDispatch } from "../../../../store/hooks";
import { changeLoaderState } from "../../../../reducers/appStateReducer";

export default function Withdraw() {
  const dispatch = useAppDispatch();
  type Filters = {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  };

  const Default_Filters: Filters = {
    searchText: null,
    fromDate: null,
    toDate: null,
    statuses: [] as string[],
  };

  const [loading, setLoading] = useState(true);
  const [openRowIds, setOpenRowIds] = useState<string[]>([]);
  const [withdrawHistoryData, setWithdrawHistoryData] =
    useState<Withdrawhistorylistresponse | null>(null);
  const [pageNo, setPageNo] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filters, setFilters] = useState<Filters>(Default_Filters);

  const [ValidateTransactionUpdateData, setValidateTransactionUpdateResponse] =
    useState<Record<string, ValidateTransactionUpdateResponse | null>>({});
  const [selectedDetailsItemId, setSelectedDetailsItemId] = useState<
    string | null
  >(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  const latestWithdrawUpdate = useRealTimeStore(state => state.latestWithdrawUpdate);
  const withdrawRevert = useRealTimeStore(state => state.withdrawRevertAllowed);
  const [isRevertAllowed, setIsRevertAllowed] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isRequestUpdateModalOpen, setIsRequestUpdateModalOpen] =
    useState(false);
  const [lastWithdrawalResponse, setLastWithdrawalResponse] =
    useState<LastWithdrawalResponse | null>(null);

  const handleOpenRowIds = (itemId: string, isOpen: boolean) => {
    setOpenRowIds((prev) => {
      if (isOpen) {
        if (!prev.includes(itemId)) {
          return [...prev, itemId];
        }
        return prev;
      } else {
        return prev.filter((id) => id !== itemId);
      }
    });
  };

  function createWithdrawHistoryRequest(
    pageNo: number,
    filters: {
      searchText: string | null;
      fromDate: string | null;
      toDate: string | null;
      statuses: string[];
    },
  ): UserHistoryListRequest {
    return {
      pageNo: pageNo,
      pageSize: 20,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      transactionId: filters.searchText?.trim() || null,
      status: filters.statuses.length ? filters.statuses.join(",") : null,
      isCallFromMobileDevice: false,
    };
  }

  const handleFiltersChange = (updated: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...updated }));
  };

  const fetchData = async (isReset = false, appliedFilters = filters) => {
    if (isLoadingMore && !isReset) {
      return;
    }

    if (isReset) {
      setLoading(true);
      dispatch(changeLoaderState(true));
    } else {
      setIsLoadingMore(true);
    }

    try {
      await getUserLastWithdrawalData();
      const currentPageNo = isReset ? 0 : pageNo;
      const request = createWithdrawHistoryRequest(currentPageNo, appliedFilters);
      const response = await getWithdrawHistory(request);

      const newData: Withdrawhistorylistresponse | null = response
        ? {
            details: response.details ?? [],
            isrevertallowed: response.isrevertallowed ?? false,
          }
        : null;

      if (newData) {
        setWithdrawHistoryData((prev) => {
          if (isReset || !prev) {
            return newData;
          }
          return {
            ...prev,
            details: [...prev.details, ...newData.details],
          };
        });
      }

      if (!newData || newData.details.length < 20) {
        setHasMore(false);
      }
      setPageNo((prev) => (isReset ? 1 : prev + 1));
    } finally {
      if (isReset) {
        setLoading(false);
        dispatch(changeLoaderState(false));
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const resetPagination = () => {
    setWithdrawHistoryData(null);
    setPageNo(0);
    setHasMore(true);

    // Reset UI state for open cards.
    setOpenRowIds([]);
    setValidateTransactionUpdateResponse({});
    setSelectedDetailsItemId(null);
    setSelectedTransactionId(null);
  };

  const handleApply = async (appliedFilters: {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  }) => {
    setFilters(appliedFilters);
    resetPagination();

    await fetchData(true, appliedFilters);
  };

  const handleRevertConfirmation = async (
    id: string | null,
    status: string,
  ) => {
    if (!id || !selectedDetailsItemId) {
      return;
    }

    const withdrawcancelrequest: Withdrawcancelrequest = {
      id: id,
      status: status,
      isdragon: lastWithdrawalResponse?.isdragon ?? false,
      dragontransactionid: lastWithdrawalResponse?.pgtransactionid ?? null,
    };

    let result = await UserWithdrawRequestUpdateStatus(withdrawcancelrequest);
    setIsConfirmationModalOpen(false);
    handleOpenRowIds(selectedDetailsItemId, false);
    setSelectedDetailsItemId(null);
    if (result) {
      toast.success(localization.withdraw_success_reverted);
      userPendingStore.getState().fetchPendingData();

      // Refresh data
      setFilters(Default_Filters);
      resetPagination();
      await fetchData(true);
    }
  };

  const getUserLastWithdrawalData = async () => {
    let data = await getUserLastWithdrawal();
    setLastWithdrawalResponse(data);
    setIsRevertAllowed(data?.isrevertallowed ?? false);
  };

  const handleCardToggle = async (itemId: string, isOpen: boolean) => {
    if (isOpen) {
      let data = await validateUsersWithdrawTransactionUpdateById(itemId);
      if (data) {
        setValidateTransactionUpdateResponse((prev) => ({
          ...prev,
          [itemId]: data,
        }));
      }
    } else {
      setValidateTransactionUpdateResponse((prev) => {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      });
    }

    handleOpenRowIds(itemId, isOpen);
  };

  const submitWithdrawTransactionUpdateRequest = async (data: {
    userRemarks: string;
    file: File | null;
  }) => {
    if (!selectedTransactionId || !selectedDetailsItemId) {
      return;
    }

    const withdrawTransactionUpdateRequest: WithdrawTransactionUpdateRequest =
      {};
    const uploadDocumentRequest: UploadDocumentRequest = {};

    if (data.file) {
      uploadDocumentRequest.file = data.file;
      uploadDocumentRequest.fileName = `withdraw-${selectedTransactionId}`;

      let imageResult = await uploadTransactionUpdateDocument(
        uploadDocumentRequest,
      );

      if (imageResult) {
        withdrawTransactionUpdateRequest.transactionupdateimage = imageResult;
      }
    }

    withdrawTransactionUpdateRequest.transactionid = selectedTransactionId;
    withdrawTransactionUpdateRequest.withdrawid = selectedDetailsItemId;
    withdrawTransactionUpdateRequest.userremarks = data.userRemarks;

    let result = await addUsersWithdrawTransactionUpdateRequest(
      withdrawTransactionUpdateRequest,
    );
    if (result) {
      toast.success(localization.withdraw_transaction_update_sent);
      setIsRequestUpdateModalOpen(false);
      handleOpenRowIds(selectedDetailsItemId, false);
      setSelectedTransactionId(null);
      setSelectedDetailsItemId(null);

      // Refresh data
      setFilters(Default_Filters);
      resetPagination();
      await fetchData(true);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchData(true);
    };

    init();
  }, []);

  const getProcessedDate = (
    item: Withdrawhistorylistdetails,
  ): string | null => {
    if (
      item.status &&
      (item.status === TransactionStatusTypes.APPROVED ||
        item.status === TransactionStatusTypes.REJECTED) &&
      item.approveddate
    ) {
      return item.approveddate;
    }

    if (item.status === TransactionStatusTypes.VERIFIED && item.verifieddate) {
      return item.verifieddate;
    }

    return item.modifieddate ?? null;
  };

  const onRevertButtonClick = async (itemId: string) => {
    await getUserLastWithdrawalData();
    setSelectedDetailsItemId(itemId);
    setIsConfirmationModalOpen(true);
  };

  useEffect(() => {
    setIsRevertAllowed(withdrawRevert);
  }, [withdrawRevert]);

  useEffect(() => {
    if (!latestWithdrawUpdate) return;
    if (!withdrawHistoryData?.details?.length) return;

    setWithdrawHistoryData((prev) => {
      if (!prev?.details) return prev;

      const index = prev.details.findIndex(
        (item) => item.id === latestWithdrawUpdate.id,
      );

      if (index === -1) return prev;

      const updatedDetails = [...prev.details];

      updatedDetails[index] = {
        ...updatedDetails[index],
        status: latestWithdrawUpdate.status,
        amount: latestWithdrawUpdate.amount,
        approveddate:
          latestWithdrawUpdate.processedDate ??
          updatedDetails[index].approveddate,
        remarks: latestWithdrawUpdate.remarks ?? updatedDetails[index].remarks,
        utrnumber:
          latestWithdrawUpdate.utrNumber ?? updatedDetails[index].utrnumber,
      };

      return {
        ...prev,
        details: updatedDetails,
      };
    });
  }, [latestWithdrawUpdate]);

  return (
    <div>
      <div>
        <TransactionFilter
          filters={filters}
          onFiltersChange={handleFiltersChange}
          statusOptions={[
            localization.pending,
            localization.approved,
            localization.rejected,
            localization.reverted,
            localization.verified,
            localization.hold,
            localization.processing,
          ]}
          onApply={handleApply}
        />
      </div>

      <InfiniteScroll
        onLoadMore={() => fetchData()}
        hasMore={hasMore}
        isLoading={loading || isLoadingMore}
      >
        {!loading && (withdrawHistoryData?.details?.length ?? 0) === 0 && (
          <div className="text-xl flex justify-center mt-1">
            {localization.no_withdraw_history}
          </div>
        )}
        <div className="grid grid-cols-1 md:landscape:grid-cols-2 lg:portrait:grid-cols-1 lg:landscape:grid-cols-2 xl:grid-cols-2 gap-4">
          {(withdrawHistoryData?.details?.length ?? 0) > 0 &&
            withdrawHistoryData?.details.map((item) => {
                const validateTransactionUpdate =
                  ValidateTransactionUpdateData[item.id];
                return (
                  <div key={item.id}>
                    <TransactionCard
                      onToggleChange={(isOpen) =>
                        handleCardToggle(item.id, isOpen)
                      }
                    >
                      <div className="grid grid-rows-2 gap-2 text-white">
                        <div className="grid grid-cols-2">
                          <div>
                            <span className="font-semibold text-sm md:landscape:text-xs lg:landscape:text-sm lg:portrait:text-base xl:landscape:text-base text-[#ECECFF] tracking-wide">
                              {item.transactionid}
                              {openRowIds.includes(item.id) && (
                                <img
                                  src={"/icons/ic-copy.svg"}
                                  alt="Copy"
                                  className="size-4 inline ml-2 cursor-pointer"
                                  onClick={() =>
                                    common.copyIdToClipboard(
                                      item.transactionid,
                                      localization.transid_success_copied,
                                    )
                                  }
                                />
                              )}
                            </span>
                          </div>
                          <div>
                            {isRevertAllowed &&
                              ![
                                TransactionStatusTypes.APPROVED,
                                TransactionStatusTypes.REJECTED,
                                TransactionStatusTypes.REVERTED,
                                TransactionStatusTypes.HOLD,
                                TransactionStatusTypes.PROCESSING,
                                TransactionStatusTypes.IN_PROGRESS,
                              ].includes(item.status) && (
                                <Button
                                  onClick={() => onRevertButtonClick(item.id)}
                                  className="flex float-right font-medium ml-2 xl:mb-0 [@media(max-width:360px)]:mb-2 sm:mb-2 p-1 text-xs md:portrait:text-sm md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-sm rounded-sm bg-[#341A00] text-[#F69C42]"
                                >
                                  <img
                                    src="/icons/ic-revert.svg"
                                    alt="Revert"
                                    className="size-4 inline mr-0.5"
                                  />
                                  {localization.revert}
                                </Button>
                              )}

                            <span
                              className={`float-right font-medium p-1 text-xs md:portrait:text-sm md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-sm rounded-sm ${WITHDRAW_STATUS_STYLE[item.status]
                                }`}
                            >
                              {localization.getString(
                                item.status.toLowerCase(),
                              )}
                            </span>
                          </div>
                        </div>

                        <div className="font-medium text-sm text-[#A9A9A9]">
                          <span>
                            {formatUtcToLocalDateTime(item.transactiondate)}
                          </span>
                          <span className="float-right text-[#E6E6E6]">
                            {`${item.amount.toFixed(2)}\u20B9`}
                          </span>
                        </div>

                        {openRowIds.includes(item.id) && (
                          <div className="grid grid-rows-2 gap-2 border-t border-[#353535] mt-2 pt-2 font-medium text-sm text-[#A9A9A9]">
                            <div>
                              <span>{localization.fee_type}</span>
                              <span className="float-right inline-flex gap-x-2">
                                <img
                                  src={
                                    item?.withdrawfeeimage
                                      ? `${BLOB_IMAGE_URL}${BlobContainerConsts.WithdrawFee}/${item.withdrawfeeimage}`
                                      : "/images/icon-upload.png"
                                  }
                                  alt="Fee Type"
                                  className="size-6"
                                />
                                {item.feetype}
                              </span>
                            </div>
                            <div>
                              <span>{localization.fee}</span>
                              <span className="float-right text-white">
                                {item.iswaiver
                                  ? 0
                                  : `${item.fee?.toFixed(2)}\u20B9`}
                              </span>
                            </div>
                            <div>
                              <span>{localization.withdraw_type}</span>
                              <img
                                src={
                                  item?.withdrawtypeimage
                                    ? `${BLOB_IMAGE_URL}${BlobContainerConsts.WithdrawType}/${item.withdrawtypeimage}`
                                    : "/images/icon-upload.png"
                                }
                                alt="Withdraw Type"
                                className="size-6 float-right"
                              />
                            </div>
                            <div>
                              <span>
                                {item.accountnumber
                                  ? localization.bank_account_id
                                  : localization.wallet_address}
                              </span>
                              <span className="float-right text-white">
                                {item.accountnumber ??
                                  item.walletaddress ??
                                  "-"}
                              </span>
                            </div>
                            <div>
                              <span>{localization.amount_after_fee}</span>
                              <span className="float-right text-white">
                                {`${item.realamount?.toFixed(2)}\u20B9`}
                              </span>
                            </div>

                            <div>
                              <span>{localization.processed_date}</span>
                              <span className="float-right text-white">
                                {[
                                  TransactionStatusTypes.APPROVED,
                                  TransactionStatusTypes.REJECTED,
                                  TransactionStatusTypes.REVERTED,
                                ].includes(item.status)
                                  ? (() => {
                                    const processedDate =
                                      getProcessedDate(item);
                                    return processedDate
                                      ? formatUtcToLocalDateTime(
                                        processedDate,
                                      )
                                      : "-";
                                  })()
                                  : "-"}
                              </span>
                            </div>

                            {item.status ===
                              TransactionStatusTypes.REJECTED && (
                                <div>
                                  <span>{localization.remark}</span>
                                  <span className="float-right text-white">
                                    {item.remarks}
                                  </span>
                                </div>
                              )}

                            {validateTransactionUpdate &&
                              validateTransactionUpdate.isUpdateVisible && (
                                <div>
                                  {!validateTransactionUpdate.isDisabled && (
                                    <div>
                                      <Button
                                        onClick={() => {
                                          setSelectedTransactionId(
                                            item.transactionid,
                                          );
                                          setSelectedDetailsItemId(item.id);
                                          setIsRequestUpdateModalOpen(true);
                                        }}
                                        className="w-full font-medium text-sm md:text-base border-2 border-[#9E59B7] rounded-[4px] bg-black text-white my-2 py-2 tracking-wide"
                                      >
                                        <img
                                          src="/icons/ic-repeat.svg"
                                          alt="Repeat"
                                          className="size-4 inline mr-2"
                                        />
                                        {localization.request_update_btn}
                                      </Button>
                                    </div>
                                  )}

                                  {validateTransactionUpdate.isDisabled && (
                                    <div className="text-center my-2 text-[#DDCD32]">
                                      <p className="mx-4">
                                        {` ${localization.update_transaction_text1} ${localization.update_transaction_text2}`}
                                      </p>
                                      <p>
                                        {localization.update_transaction_text3}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    </TransactionCard>
                  </div>
                );
            })}
        </div>
      </InfiniteScroll>

      {isConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onConfirm={() =>
            handleRevertConfirmation(
              lastWithdrawalResponse?.id ?? null,
              lastWithdrawalResponse?.status ?? "",
            )
          }
          onCancel={() => {
            setIsConfirmationModalOpen(false);
            setSelectedDetailsItemId(null);
          }}
          title={localization.withdraw_revert_confirmation_lable}
          icon={"/icons/ic-info.svg"}
          blurColor="bg-[#FFE6CC]"
          confirmText={localization.yes.toUpperCase()}
          cancelText={localization.no.toUpperCase()}
        />
      )}

      {isRequestUpdateModalOpen && (
        <UploadDocumentModal
          isOpen={isRequestUpdateModalOpen}
          onSubmit={(data) => submitWithdrawTransactionUpdateRequest(data)}
          onCancel={() => {
            setIsRequestUpdateModalOpen(false);
            setSelectedTransactionId(null);
            setSelectedDetailsItemId(null);
          }}
          title={localization.upload_document_text1}
          warningText={localization.upload_document_text2}
          blurColor="bg-[#DBEEFF]"
          submitText={localization.upload_without_attachment_btn}
        />
      )}
    </div>
  );
}
